import { IconRotateClockwise, IconTrash } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import type { ImageClickMapAnswerValue } from '../../lib/question-blocks';
import type { RespondentField } from '../../lib/multimodal-contract';
import './ImageClickMapControl.css';

interface ImageClickMapControlProps {
  field: RespondentField;
  value: ImageClickMapAnswerValue;
  onChange: (value: ImageClickMapAnswerValue) => void;
  onSubmit: (value: ImageClickMapAnswerValue) => void;
  disabled: boolean;
  submitLabel: string;
}

/** Whether the click surface image can take points, is still loading, or needs a retry. */
type MediaState = 'loading' | 'ready' | 'failed';

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt';
const compactIconAction = `${focus} h-9 w-9 cursor-pointer rounded-lg text-muted-foreground hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50`;
const submitAction = `${focus} inline-flex cursor-pointer items-center justify-center rounded-lg px-4 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-50`;

function coordinate(value: number): number {
  return Math.round(Math.max(0, Math.min(1, value)) * 1_000_000) / 1_000_000;
}

function position(element: HTMLElement, clientX: number, clientY: number): { x: number; y: number } | undefined {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return undefined;
  return {
    x: coordinate((clientX - rect.left) / rect.width),
    y: coordinate((clientY - rect.top) / rect.height),
  };
}

function samePoint(first: { x: number; y: number }, second: { x: number; y: number }): boolean {
  return first.x === second.x && first.y === second.y;
}

/** Direct image placement with independently focusable, draggable markers. */
export function ImageClickMapControl({ field, value, onChange, onSubmit, disabled,
  submitLabel }: ImageClickMapControlProps): React.ReactElement {
  const imageIdentity = `${field.fieldId}:${field.image?.imageUrl ?? ''}`;
  // Readiness is scoped to one image identity instead of reset by an effect, so a cached image
  // whose load lands before passive effects run cannot be pushed back to loading.
  const [media, setMedia] = useState<{ identity: string; state: MediaState }>(() => ({ identity: imageIdentity, state: 'loading' }));
  const mediaState: MediaState = media.identity === imageIdentity ? media.state : 'loading';
  const [keyboardPoint, setKeyboardPoint] = useState({ x: 0.5, y: 0.5 });
  const overlay = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointerId: number; index: number; original: { x: number; y: number } } | null>(null);
  const min = field.minClicks ?? 1;
  const max = field.maxClicks ?? 1;
  const ready = mediaState === 'ready';
  const valid = value.points.length >= min && value.points.length <= max;

  useEffect(() => {
    setKeyboardPoint({ x: 0.5, y: 0.5 });
    drag.current = null;
  }, [imageIdentity]);

  const add = (next: { x: number; y: number }): void => {
    if (disabled || !ready || value.points.length >= max || value.points.some(item => samePoint(item, next))) return;
    onChange({ points: [...value.points, next] });
  };
  const move = (index: number, next: { x: number; y: number }): void => {
    if (disabled || !ready || value.points.some((item, candidate) => candidate !== index && samePoint(item, next))) return;
    onChange({ points: value.points.map((item, candidate) => candidate === index ? next : item) });
  };
  const remove = (index: number): void => {
    if (disabled || !ready) return;
    onChange({ points: value.points.filter((_, candidate) => candidate !== index) });
    overlay.current?.focus();
  };

  if (!field.image) return <p role="alert" className="text-sm text-danger">Image unavailable</p>;

  return <div className="space-y-4">
    <div className="relative inline-block max-w-full overflow-hidden rounded-lg bg-surface align-top">
      {mediaState !== 'failed' ? <img key={imageIdentity} src={field.image.imageUrl} alt=""
        className={ready ? 'block h-auto max-h-[28rem] max-w-full object-contain'
          : 'absolute inset-0 h-full w-full object-contain'}
        onLoad={event => {
          // A hidden in-call panel lays a decoded image out at 0x0, so layout alone cannot gate readiness.
          const image = event.currentTarget;
          const rect = image.getBoundingClientRect();
          const decoded = image.naturalWidth > 0 && image.naturalHeight > 0;
          const rendered = rect.width > 0 && rect.height > 0;
          setMedia({ identity: imageIdentity, state: decoded || rendered ? 'ready' : 'failed' });
        }}
        onError={() => setMedia({ identity: imageIdentity, state: 'failed' })} /> : null}
      {!ready ? <div role={mediaState === 'failed' ? undefined : 'status'}
        aria-label={mediaState === 'failed' ? undefined : 'Loading image'} data-failed={mediaState === 'failed'}
        className="image-click-map-loading flex min-h-40 min-w-64 flex-col items-start justify-center gap-2 px-4 text-sm text-muted-foreground">
        {mediaState === 'failed' ? <><span>Image unavailable</span><button type="button"
          className={`${focus} cursor-pointer underline`} onClick={() => setMedia({ identity: imageIdentity, state: 'loading' })}>Retry image</button></>
          : <span className="sr-only">Loading image</span>}
      </div> : null}
      <div ref={overlay} role="application" aria-label={field.image.alt} aria-disabled={disabled || !ready}
        tabIndex={disabled || !ready ? -1 : 0}
        className={`${focus} image-click-map-surface absolute inset-0 touch-none ${ready && !disabled ? 'cursor-crosshair' : 'pointer-events-none cursor-default'}`}
        onPointerDown={event => {
          if (event.button !== 0 || disabled || !ready) return;
          const next = position(event.currentTarget, event.clientX, event.clientY);
          if (next) add(next);
        }}
        onKeyDown={event => {
          if (event.target !== event.currentTarget || disabled || !ready) return;
          const delta = event.shiftKey ? 0.1 : 0.02;
          if (event.key === 'ArrowLeft') setKeyboardPoint(item => ({ ...item, x: coordinate(item.x - delta) }));
          else if (event.key === 'ArrowRight') setKeyboardPoint(item => ({ ...item, x: coordinate(item.x + delta) }));
          else if (event.key === 'ArrowUp') setKeyboardPoint(item => ({ ...item, y: coordinate(item.y - delta) }));
          else if (event.key === 'ArrowDown') setKeyboardPoint(item => ({ ...item, y: coordinate(item.y + delta) }));
          else if (event.key === 'Enter' || event.key === ' ') add(keyboardPoint);
          else return;
          event.preventDefault();
        }}>
        {ready ? value.points.map((item, index) => <button key={index} type="button" aria-label={`Point ${index + 1}`}
          disabled={disabled} className={`${focus} image-click-map-marker absolute h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full bg-transparent active:cursor-grabbing disabled:cursor-default`}
          style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%` }}
          onPointerDown={event => {
            event.stopPropagation();
            if (event.button !== 0 || disabled || drag.current) return;
            drag.current = { pointerId: event.pointerId, index, original: item };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={event => {
            const active = drag.current;
            if (!active || active.pointerId !== event.pointerId || active.index !== index || !overlay.current) return;
            const next = position(overlay.current, event.clientX, event.clientY);
            if (next) move(index, next);
          }}
          onPointerUp={event => {
            const active = drag.current;
            if (!active || active.pointerId !== event.pointerId || active.index !== index) return;
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            drag.current = null;
          }}
          onPointerCancel={event => {
            const active = drag.current;
            if (!active || active.pointerId !== event.pointerId || active.index !== index) return;
            drag.current = null;
            move(index, active.original);
          }}
          onKeyDown={event => {
            const delta = event.shiftKey ? 0.1 : 0.02;
            let next: { x: number; y: number } | undefined;
            if (event.key === 'ArrowLeft') next = { ...item, x: coordinate(item.x - delta) };
            else if (event.key === 'ArrowRight') next = { ...item, x: coordinate(item.x + delta) };
            else if (event.key === 'ArrowUp') next = { ...item, y: coordinate(item.y - delta) };
            else if (event.key === 'ArrowDown') next = { ...item, y: coordinate(item.y + delta) };
            else if (event.key === 'Delete' || event.key === 'Backspace') remove(index);
            else return;
            event.stopPropagation();
            event.preventDefault();
            if (next) move(index, next);
          }} />) : null}
        {ready && value.points.length < max ? <span aria-hidden="true"
          className="image-click-map-keyboard-cursor absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-foreground"
          style={{ left: `${keyboardPoint.x * 100}%`, top: `${keyboardPoint.y * 100}%` }} /> : null}
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" aria-label="Undo point" className={compactIconAction}
        disabled={disabled || !ready || !value.points.length}
        onClick={() => onChange({ points: value.points.slice(0, -1) })}>
        <IconRotateClockwise aria-hidden="true" className="mx-auto h-4 w-4 -scale-x-100" />
      </button>
      <button type="button" aria-label="Clear points" className={compactIconAction}
        disabled={disabled || !ready || !value.points.length} onClick={() => onChange({ points: [] })}>
        <IconTrash aria-hidden="true" className="mx-auto h-4 w-4" />
      </button>
      <button type="button" className={`${submitAction} button-accent`} disabled={disabled || !ready || !valid}
        onClick={() => onSubmit(value)}>{submitLabel}</button>
    </div>
  </div>;
}
